import { logger } from './logger';

export interface NormalizedPracticeData {
  task: string;
  starter: string;
  solution?: string;
  hints: string[];
  estimatedTime?: number;
  difficulty?: string;
  type?: string;
  starterFiles?: Record<string, string>;
  solutionFiles?: Record<string, string>;
}

/**
 * Normalizes practice lab data from various JSONB structures in the database
 * to a consistent format for UI consumption
 */
export class PracticeDataNormalizer {
  /**
   * Normalize practice data from any supported structure
   */
  static normalize(rawData: any, courseType?: string): NormalizedPracticeData | null {
    if (!rawData) {
      logger.warn('PracticeDataNormalizer: No data provided');
      return null;
    }

    // If already in correct format, return as-is
    if (this.isValidNormalizedFormat(rawData)) {
      return rawData as NormalizedPracticeData;
    }

    // Try different normalization strategies (prioritize starter_files format - 69% of records)
    const normalized = 
      this.normalizeFromStarterFiles(rawData) ||
      this.normalizeFromExercises(rawData) ||
      this.normalizeFromScenarios(rawData) ||
      this.normalizeFromTasks(rawData) ||
      this.normalizeFromLegacyFormat(rawData) ||
      this.normalizeFromDirectFields(rawData);

    if (normalized) {
      logger.debug('PracticeDataNormalizer: Successfully normalized data', {
        sourceType: this.detectSourceType(rawData),
        courseType
      });
      return normalized;
    }

    logger.error('PracticeDataNormalizer: Failed to normalize data', { rawData });
    return null;
  }

  /**
   * Check if data is already in normalized format
   */
  private static isValidNormalizedFormat(data: any): boolean {
    return (
      typeof data === 'object' &&
      typeof data.task === 'string' &&
      typeof data.starter === 'string' &&
      Array.isArray(data.hints)
    );
  }

  /**
   * NEW: Normalize from starter_files format (69% of practice labs use this)
   * This format has: starter_files object, instructions object, solution_files object
   */
  private static normalizeFromStarterFiles(data: any): NormalizedPracticeData | null {
    if (!data.starter_files || typeof data.starter_files !== 'object') {
      return null;
    }

    // Extract starter code from first available file
    const starterFileNames = Object.keys(data.starter_files);
    if (starterFileNames.length === 0) {
      return null;
    }

    // Prioritize main/index files
    const mainStarterFile = starterFileNames.find(f => 
      f.includes('main') || f.includes('index') || f.includes('setup') || f.includes('app')
    ) || starterFileNames[0];
    const starterCode = data.starter_files[mainStarterFile] || '';

    // Extract solution from solution_files if available
    let solutionCode: string | undefined;
    let solutionFiles: Record<string, string> | undefined;
    if (data.solution_files && typeof data.solution_files === 'object') {
      solutionFiles = data.solution_files;
      const solutionFileNames = Object.keys(data.solution_files);
      const mainSolutionFile = solutionFileNames.find(f =>
        f.includes('solution') || f.includes('answer') || f.includes('main')
      ) || solutionFileNames[0];
      solutionCode = data.solution_files[mainSolutionFile];
    }

    // Build task from description and instructions
    let task = '';
    if (data.description) {
      task = data.description;
    } else if (data.instructions?.overview) {
      task = data.instructions.overview;
    } else if (data.title) {
      task = data.title;
    } else {
      task = 'Complete the practice exercise';
    }

    // Extract hints from instruction steps
    const hints: string[] = [];
    if (data.instructions?.steps && Array.isArray(data.instructions.steps)) {
      hints.push(...data.instructions.steps.slice(0, 5));
    }
    if (data.instructions?.expected_outcome) {
      hints.push(`Expected outcome: ${data.instructions.expected_outcome}`);
    }
    // Add any explicit hints
    if (Array.isArray(data.hints)) {
      hints.push(...data.hints);
    }

    return {
      task,
      starter: starterCode,
      solution: solutionCode,
      hints: hints.length > 0 ? hints : ['Review the instructions', 'Check the documentation', 'Break down the problem'],
      estimatedTime: data.estimated_time || data.estimatedTime,
      difficulty: data.difficulty_level || data.difficulty,
      type: 'starter_files',
      starterFiles: data.starter_files,
      solutionFiles
    };
  }

  /**
   * Normalize from exercises array format
   */
  private static normalizeFromExercises(data: any): NormalizedPracticeData | null {
    if (!Array.isArray(data.exercises) || data.exercises.length === 0) {
      return null;
    }

    const firstExercise = data.exercises[0];
    
    return {
      task: firstExercise.task || firstExercise.description || firstExercise.title || 'Complete the practice exercise',
      starter: firstExercise.starter || firstExercise.starterCode || firstExercise.starter_code || '',
      solution: firstExercise.solution || firstExercise.solutionCode || firstExercise.solution_code,
      hints: firstExercise.hints || [],
      estimatedTime: data.estimated_time || data.estimatedTime,
      difficulty: data.difficulty_level || data.difficulty,
      type: 'exercises'
    };
  }

  /**
   * Normalize from scenarios array format
   */
  private static normalizeFromScenarios(data: any): NormalizedPracticeData | null {
    if (!Array.isArray(data.scenarios) || data.scenarios.length === 0) {
      return null;
    }

    const firstScenario = data.scenarios[0];
    
    return {
      task: firstScenario.scenario || firstScenario.description || 'Complete the scenario',
      starter: firstScenario.starter_code || firstScenario.starter || '',
      solution: firstScenario.solution,
      hints: firstScenario.hints || [],
      estimatedTime: data.estimated_time || data.estimatedTime,
      difficulty: data.difficulty_level || data.difficulty,
      type: 'scenarios'
    };
  }

  /**
   * Normalize from tasks array format
   */
  private static normalizeFromTasks(data: any): NormalizedPracticeData | null {
    if (!Array.isArray(data.tasks) || data.tasks.length === 0) {
      return null;
    }

    const firstTask = data.tasks[0];
    
    return {
      task: firstTask.description || firstTask.task || 'Complete the task',
      starter: firstTask.starter_code || firstTask.starter || '',
      solution: firstTask.solution,
      hints: firstTask.hints || [],
      estimatedTime: data.estimated_time || data.estimatedTime,
      difficulty: data.difficulty_level || data.difficulty,
      type: 'tasks'
    };
  }

  /**
   * Normalize from legacy format (direct fields at root level)
   */
  private static normalizeFromLegacyFormat(data: any): NormalizedPracticeData | null {
    // Check if data has direct fields (no nested arrays)
    if (data.task || data.description || data.starter || data.starter_code) {
      return {
        task: data.task || data.description || 'Complete the practice lab',
        starter: data.starter || data.starter_code || data.starterCode || '',
        solution: data.solution || data.solution_code || data.solutionCode,
        hints: data.hints || [],
        estimatedTime: data.estimated_time || data.estimatedTime,
        difficulty: data.difficulty_level || data.difficulty,
        type: 'legacy'
      };
    }

    return null;
  }

  /**
   * Normalize from direct fields (flat structure) - Enhanced fallback
   */
  private static normalizeFromDirectFields(data: any): NormalizedPracticeData | null {
    if (typeof data !== 'object' || data === null) {
      return null;
    }

    // Extract task from multiple possible fields
    const task = data.task || 
      data.description || 
      data.instructions?.overview ||
      data.title ||
      '';

    // Extract starter from multiple possible fields
    const starter = data.starter || 
      data.starter_code || 
      data.starterCode || 
      data.code ||
      this.getDefaultStarterForPracticeType(data.practice_type) ||
      '';
    
    // Extract hints from instructions.steps if available
    let hints: string[] = [];
    if (Array.isArray(data.hints)) {
      hints = data.hints;
    } else if (data.instructions?.steps && Array.isArray(data.instructions.steps)) {
      hints = data.instructions.steps;
    }

    // Require at least a task description
    if (!task) {
      return null;
    }

    return {
      task: task || 'Complete the practice exercise',
      starter: starter,
      solution: data.solution || data.solution_code || data.solutionCode,
      hints: hints.length > 0 ? hints : ['Review module content', 'Check documentation', 'Break down the problem'],
      estimatedTime: data.estimated_time || data.estimatedTime,
      difficulty: data.difficulty_level || data.difficulty,
      type: 'direct'
    };
  }

  /**
   * Get default starter code based on practice type
   */
  private static getDefaultStarterForPracticeType(practiceType?: string): string {
    const starters: Record<string, string> = {
      'coding': '// Write your code here\n',
      'exercise': '// Complete the exercise below\n',
      'project': '// Project starter code\n',
      'sql': '-- Write your SQL query here\n',
      'python': '# Write your Python code here\n',
      'java': '// Write your Java code here\npublic class Main {\n    public static void main(String[] args) {\n        // Your code here\n    }\n}\n'
    };
    return starters[practiceType?.toLowerCase() || ''] || '';
  }

  /**
   * Detect the source type of the data structure
   */
  private static detectSourceType(data: any): string {
    if (data.starter_files) return 'starter_files';
    if (Array.isArray(data.exercises)) return 'exercises';
    if (Array.isArray(data.scenarios)) return 'scenarios';
    if (Array.isArray(data.tasks)) return 'tasks';
    if (data.task || data.starter) return 'direct';
    if (data.description || data.instructions) return 'description_only';
    return 'unknown';
  }

  /**
   * Validate that normalized data has minimum required fields
   */
  static validate(data: NormalizedPracticeData | null): boolean {
    if (!data) return false;
    
    const hasTask = typeof data.task === 'string' && data.task.length > 0;
    const hasStarter = typeof data.starter === 'string';
    const hasHints = Array.isArray(data.hints);
    
    return hasTask && hasStarter && hasHints;
  }
}
